import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";

import "./ImageUpload.scss";
import FileInput from "../file-input/FileInput";
import useThemeService from "../../services/theme-service/ThemeService";
import RemoveOrUndoImage from "./components/RemoveOrUndo";
import Image from "./components/Image";
import { ImageUploadState } from "./ImageUploadModel";
import { snackbarNotificationsService } from "../../services/notifications/SnackbarNotificationsService";

export interface ImageWithPreview extends File {
  preview: string;
}

interface ImageUploadProps {
  placeholderImage?: React.FunctionComponent;
  initialImageUri?: string;
  imageUploadState: ImageUploadState;
  className?: string;
}

export default observer(function ImageUpload(props: ImageUploadProps) {
  const { themeClass } = useThemeService();

  const { initialImageUri, placeholderImage, imageUploadState, className } = props;

  const itemHasImage = useMemo(() => !!initialImageUri, [initialImageUri]);
  const canRemoveImage = useMemo(
    () =>
      !imageUploadState.shouldDeleteCurrentImage &&
      (itemHasImage || !!imageUploadState.imageForUpload),
    [imageUploadState, itemHasImage]
  );

  const handleChangeImage = (fileList: FileList | null) => {
    if (!fileList) return;

    const image = fileList[0];
    if (!image.type.startsWith("image/")) {
      snackbarNotificationsService.showError("general.upload.no-image-file-type");
      return;
    }

    const imageWithPreview = Object.assign(image, {
      preview: URL.createObjectURL(image),
    });
    imageUploadState.upload(imageWithPreview);
  };

  useEffect(() => {
    if (!initialImageUri) return;

    async function base64ToBlob(imageUri: string) {
      const base64UriResponse = await fetch(imageUri);
      const imageBlob = await base64UriResponse.blob();
      const imageFile = new File([imageBlob], `${crypto.randomUUID()}.jpeg`, {
        type: "image/jpeg",
      });

      const imageWithPreview = Object.assign(imageFile, {
        preview: URL.createObjectURL(imageFile),
      });
      imageUploadState.upload(imageWithPreview);
    }

    base64ToBlob(initialImageUri);
  }, [imageUploadState, initialImageUri]);

  return (
    <section className={themeClass(["image-upload", className ? className : ""])}>
      <Image
        initialImageUri={initialImageUri}
        uploadImageUri={imageUploadState.imageForUpload?.preview}
        placeholderImage={placeholderImage}
      />
      <FileInput accept="image/*" labelKey="general.upload-picture" onChange={handleChangeImage} />
      <RemoveOrUndoImage
        enableRemoveImage={canRemoveImage}
        removeImage={imageUploadState.removeImage}
        enableUndo={imageUploadState.shouldDeleteCurrentImage}
        undoRemoveImage={imageUploadState.undoRemoveImage}
      />
    </section>
  );
});
